import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight, faDownload, faCheckCircle, faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import FreeDownloads from '../FreeDownloads/FreeDownloads';
import HeroSearch from '../HeroSearch/HeroSearch';
import { ImageWithFallback } from '../MediaFallback/MediaFallback';
import blogPosts from '../../data/blogPosts';

const stats = [
    { value: '2.7M+', label: 'Photos' },
    { value: '1M+', label: 'Illustrations' },
    { value: '100K+', label: 'Videos' },
    { value: '100%', label: 'Free' },
];

const benefits = [
    { icon: faDownload, title: 'Always free', text: 'Every asset downloads instantly, in full resolution, at no cost.' },
    { icon: faCheckCircle, title: 'No attribution', text: 'Use anything for personal or commercial work — credit optional.' },
    { icon: faLayerGroup, title: 'Fresh daily', text: 'New visuals are added every day, so there is always something new to find.' },
];

// Fanned-deck rotations; image URLs are filled from live Pixabay content.
const DECK_ROTATIONS = ['-rotate-12', '-rotate-6', 'rotate-0', 'rotate-6', 'rotate-12'];

const categories = [
    { name: 'Nature', image: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=600&q=60' },
    { name: 'Business', image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=600&q=60' },
    { name: 'People', image: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?auto=format&fit=crop&w=600&q=60' },
    { name: 'Food', image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=600&q=60' },
    { name: 'Travel', image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=600&q=60' },
    { name: 'Technology', image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=600&q=60' },
    { name: 'Animals', image: 'https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=600&q=60' },
    { name: 'Architecture', image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=600&q=60' },
];

const Home = () => {
    const [deck, setDeck] = useState([]);
    const [activePost, setActivePost] = useState(0);
    const [activeCat, setActiveCat] = useState(0);
    const deckRef = useRef(null);
    const fanRef = useRef(null);

    // Parallax: as the page scrolls, the hero deck drifts up a little slower
    // than the scroll, and the fanned cards spread apart horizontally — the fan
    // gently opens as you scroll down instead of fading out. Written straight to
    // the nodes (no re-render) and rAF-throttled.
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
        let ticking = false;
        const apply = () => {
            ticking = false;
            const el = deckRef.current;
            if (!el) return;
            const y = window.scrollY;
            el.style.transform = `translateY(${(-y * 0.12).toFixed(1)}px)`;

            // Nudge each card away from the deck's center, capped so it stays a
            // subtle "opening fan" rather than flying apart.
            const fan = fanRef.current;
            if (fan) {
                const cards = fan.children;
                const center = (cards.length - 1) / 2;
                const spread = Math.min(y * 0.06, 18);
                for (let i = 0; i < cards.length; i++) {
                    const inner = cards[i].firstElementChild;
                    if (inner) inner.style.transform = `translateX(${((i - center) * spread).toFixed(1)}px)`;
                }
            }
        };
        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                window.requestAnimationFrame(apply);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        apply();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Pull a few live, popular images from Pixabay for the hero deck.
    useEffect(() => {
        const params = new URLSearchParams({
            key: process.env.REACT_APP_PIXABAY_API_KEY,
            image_type: 'photo',
            order: 'popular',
            editors_choice: 'true',
            safesearch: 'true',
            per_page: '15',
        });
        fetch(`https://pixabay.com/api/?${params.toString()}`)
            .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
            .then((data) => setDeck((data.hits || []).slice(0, DECK_ROTATIONS.length)))
            .catch((err) => console.log('Hero deck failed', err));
    }, []);

    return (
        <div>
            {/* ===== Editorial hero (two-column) ===== */}
            <section className="relative bg-white dark:bg-gray-900 text-gray-900 dark:text-white overflow-hidden">
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 sm:pt-24 sm:pb-12 grid lg:grid-cols-2 gap-12 items-center">
                    {/* left — copy */}
                    <div className="text-center lg:text-left">
                        <h1 className="font-serif text-4xl sm:text-6xl font-medium leading-tight">
                            Every visual your{' '}
                            <span className="relative whitespace-nowrap text-brand-500 dark:text-brand-400">
                                story
                                <svg
                                    className="absolute left-0 w-full"
                                    style={{ bottom: '-0.4rem' }}
                                    height="14"
                                    viewBox="0 0 200 14"
                                    preserveAspectRatio="none"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path d="M3 9 C 45 3, 80 13, 120 7 S 175 3, 197 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                            </span>{' '}
                            needs.
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 dark:text-gray-300 max-w-lg mx-auto lg:mx-0">
                            Free photos, illustrations and videos — hand-curated, beautifully organized,
                            and ready to download in seconds.
                        </p>

                        {/* primary action: modern search with autocomplete */}
                        <div className="mt-8">
                            <HeroSearch />
                        </div>
                    </div>

                    {/* right — fanned deck (live Pixabay images, hover a card to make it hop) */}
                    <div ref={deckRef} className="flex items-center justify-center lg:justify-end min-h-[10rem] sm:min-h-[15rem] will-change-transform">
                        <div ref={fanRef} className="flex items-end">
                            {deck.map((c, i) => (
                                <div
                                    key={c.id}
                                    className={`card-reveal flex-shrink-0 ${i > 0 ? '-ml-8 sm:-ml-10 lg:-ml-12' : ''}`}
                                    style={{ '--reveal-delay': `${(i * 0.12).toFixed(2)}s` }}
                                >
                                    {/* scroll-driven horizontal spread lives on its own
                                        wrapper so it never fights the reveal or hover transforms */}
                                    <div className="will-change-transform">
                                    <div className="deck-jump">
                                        <div
                                            className={`relative w-20 sm:w-32 lg:w-36 rounded-2xl overflow-hidden shadow-e3 transform transition-transform duration-500 ${DECK_ROTATIONS[i]} hover:scale-105 hover:z-20`}
                                        >
                                            <ImageWithFallback
                                                src={c.webformatURL}
                                                alt=""
                                                aria-hidden="true"
                                                className="ken-burns w-full h-36 sm:h-48 lg:h-60 object-cover"
                                                style={{ '--kb-delay': `${(i * 2.5).toFixed(1)}s` }}
                                            />
                                        </div>
                                    </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* stats */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 sm:pb-16">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 pt-6 sm:pt-10">
                        {stats.map((s) => (
                            <div key={s.label} className="text-center">
                                <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{s.value}</p>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Browse by category — grid-line tiles ===== */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-5 sm:pt-16 sm:pb-8 flex items-end justify-between">
                <div>
                    <h2 className="font-serif text-3xl sm:text-4xl font-medium text-gray-900 dark:text-white">
                        Browse by category
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Jump straight into a curated collection.</p>
                </div>
                <Link to="/photos" className="hidden sm:inline-flex items-center min-h-[44px] px-2 gap-1 rounded-full text-brand-600 dark:text-brand-400 font-semibold hover:text-brand-700 dark:hover:text-brand-500 transition-colors">
                    View all <FontAwesomeIcon icon={faArrowRight} />
                </Link>
            </div>

            <div
                className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-3 lg:h-[26rem]"
                onMouseLeave={() => setActiveCat(0)}
            >
                {categories.map((cat, i) => {
                    const expanded = i === activeCat;
                    return (
                        <Link
                            key={cat.name}
                            to={{ pathname: '/photos', search: `?q=${cat.name.toLowerCase()}` }}
                            onMouseEnter={() => setActiveCat(i)}
                            className={`group relative overflow-hidden rounded-[1.75rem] lg:rounded-[2.5rem] ring-1 ring-black/5 dark:ring-white/10 bg-gray-100 dark:bg-gray-800 h-24 lg:h-full transition-all duration-500 ease-out ${
                                expanded ? 'lg:flex-[3.5]' : 'lg:flex-1'
                            }`}
                        >
                            <ImageWithFallback
                                src={cat.image}
                                alt={cat.name}
                                label={cat.name}
                                className={`absolute inset-0 w-full h-full object-cover transform transition-transform duration-[1200ms] ${
                                    expanded ? 'scale-105' : 'scale-100'
                                }`}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent md:from-black/70" />

                            {/* label — always readable; expands when active */}
                            <div className="absolute inset-0 flex flex-col justify-end p-5">
                                <span className={`h-0.5 bg-brand-400 mb-3 origin-left transition-all duration-500 ${expanded ? 'w-12' : 'w-6'}`} />
                                <h3 className="text-white text-xl md:text-2xl font-bold tracking-wide whitespace-nowrap">
                                    {cat.name}
                                </h3>
                                <span
                                    className={`mt-2 inline-flex items-center gap-2 text-brand-400 text-sm font-semibold whitespace-nowrap overflow-hidden transition-all duration-500 ${
                                        expanded ? 'opacity-100 max-h-8' : 'opacity-0 max-h-0'
                                    }`}
                                >
                                    Explore <FontAwesomeIcon icon={faArrowRight} />
                                </span>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {/* ===== Free downloads (live bento gallery) ===== */}
            <FreeDownloads />

            {/* ===== Benefits ===== */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
                <div className="text-center mb-8 sm:mb-12">
                    <h2 className="font-serif text-3xl sm:text-4xl font-medium text-gray-900 dark:text-white">
                        Why creators choose Pixiy
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-2">Everything free, forever — no strings attached.</p>
                </div>
                <div className="relative grid sm:grid-cols-3 gap-12 sm:gap-8">
                    {/* connecting track behind the icon nodes (desktop only) */}
                    <span
                        aria-hidden="true"
                        className="hidden sm:block absolute top-8 left-[16.666%] right-[16.666%] h-px bg-gradient-to-r from-brand-400/0 via-brand-400/60 to-brand-400/0"
                    />
                    {benefits.map((b) => (
                        <div key={b.title} className="group relative text-center">
                            {/* node */}
                            <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-white dark:bg-gray-900 ring-1 ring-brand-400/40 shadow-e2 text-brand-500 dark:text-brand-400">
                                <FontAwesomeIcon icon={b.icon} size="lg" className="icon-shake" />
                            </span>
                            <h3 className="text-base font-bold uppercase tracking-[0.15em] text-gray-900 dark:text-white mt-6">{b.title}</h3>
                            <span aria-hidden="true" className="block h-0.5 w-8 bg-brand-400 mx-auto mt-3" />
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 leading-relaxed max-w-xs mx-auto">{b.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ===== From the blog ===== */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
                <div className="flex items-end justify-between mb-6 sm:mb-10">
                    <div>
                        <h2 className="font-serif text-3xl sm:text-4xl font-medium text-gray-900 dark:text-white">
                            From the blog
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">Tips, guides and inspiration for working with free visuals.</p>
                    </div>
                    <Link to="/blog" className="group hidden sm:inline-flex items-center min-h-[44px] px-2 gap-1 rounded-full text-brand-600 dark:text-brand-400 font-semibold hover:text-brand-700 dark:hover:text-brand-500 transition-colors">
                        View all
                        <FontAwesomeIcon icon={faArrowRight} className="transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>

                <div className="grid lg:grid-cols-[1fr_22rem] gap-10">
                    {/* Index list — hover a row to preview its cover */}
                    <ul className="divide-y divide-gray-200 dark:divide-gray-800 border-y border-gray-200 dark:border-gray-800">
                        {blogPosts.slice(0, 4).map((post, i) => {
                            const active = i === activePost;
                            return (
                                <li
                                    key={post.slug}
                                    onMouseEnter={() => setActivePost(i)}
                                    onFocus={() => setActivePost(i)}
                                >
                                    <Link
                                        to={`/blog/${post.slug}`}
                                        className="group relative flex items-center gap-5 py-6 pr-4"
                                    >
                                        {/* sliding green fill on hover/active */}
                                        <span
                                            aria-hidden="true"
                                            className={`absolute inset-y-0 -left-4 sm:-left-6 lg:-left-8 right-0 -z-10 origin-left rounded-r-2xl bg-brand-50 dark:bg-brand-500/10 transition-transform duration-500 ${
                                                active ? 'scale-x-100' : 'scale-x-0'
                                            }`}
                                        />
                                        <span
                                            className={`font-serif text-2xl sm:text-3xl tabular-nums w-12 shrink-0 transition-colors ${
                                                active ? 'text-brand-500' : 'text-gray-300 dark:text-gray-700'
                                            }`}
                                        >
                                            0{i + 1}
                                        </span>

                                        {/* inline thumbnail — only shown on small screens */}
                                        <div className="lg:hidden relative w-20 h-16 shrink-0 overflow-hidden rounded-xl">
                                            <img src={post.cover} alt={post.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <span className="text-[11px] font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                                                {post.tag}
                                            </span>
                                            <h3
                                                className={`font-serif text-xl sm:text-2xl font-medium leading-snug transition-all duration-300 ${
                                                    active
                                                        ? 'text-gray-900 dark:text-white translate-x-1'
                                                        : 'text-gray-500 dark:text-gray-400'
                                                }`}
                                            >
                                                {post.title}
                                            </h3>
                                            <div className="mt-1 text-xs text-gray-400">{post.date} · {post.readTime}</div>
                                        </div>

                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            className={`shrink-0 text-brand-500 transition-all duration-300 ${
                                                active ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                                            }`}
                                        />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Floating preview — swaps to the hovered post (desktop only) */}
                    <div className="hidden lg:block sticky top-24 self-start h-[26rem]">
                        <div className="relative w-full h-full overflow-hidden rounded-3xl shadow-e3 ring-1 ring-black/5">
                            {blogPosts.slice(0, 4).map((post, i) => (
                                <img
                                    key={post.slug}
                                    src={post.cover}
                                    alt={post.title}
                                    loading="lazy"
                                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                                        i === activePost ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                                    }`}
                                />
                            ))}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                            <div className="absolute bottom-0 p-6">
                                <span className="inline-flex rounded-full bg-brand-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-e2">
                                    {blogPosts[activePost].tag}
                                </span>
                                <p className="text-sm text-gray-100 mt-3 line-clamp-2">{blogPosts[activePost].excerpt}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 text-center sm:hidden">
                    <Link to="/blog" className="inline-flex items-center gap-1 text-brand-500 font-semibold hover:text-brand-600 transition-colors">
                        View all posts <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                </div>
            </section>

            {/* ===== Closing CTA ===== */}
            <section className="relative bg-white dark:bg-gray-900 text-gray-900 dark:text-white overflow-hidden">
                <div className="blueprint absolute inset-0" />
                <div className="absolute inset-0 bg-gradient-to-b from-white dark:from-gray-900 via-transparent to-white dark:to-gray-900" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center">
                    <h2 className="font-serif text-3xl sm:text-5xl font-medium leading-tight">
                        Start creating with <span className="text-brand-500 dark:text-brand-400">zero limits.</span>
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-4 max-w-xl mx-auto">
                        Millions of free assets, one search away.
                    </p>
                    <Link
                        to="/photos"
                        className="mt-9 inline-flex items-center gap-2 bg-gray-900 text-white dark:bg-white dark:text-gray-900 font-semibold px-8 py-3.5 rounded-full hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
                    >
                        Browse the library <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Home;
